import React, { FC, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Flex, Grid } from '$components/layouts';
import { WEB_URL, mq } from '$lib/helpers';
import { key } from '~/utils/key';
import { useIntersectionObserver, useTranslation } from '$hooks';
import { formatString } from '$lib/helpers';
import { Loading } from '$components/elements/loading/loading';
import { Button } from '$components/elements/button';
import { IHelloRetailCategory } from '~/modules/search/interfaces/suggestion';
import { ResultCard } from './result-types/result-card';

type Props = {
    categories?: IHelloRetailCategory[];
    categoryTotalCount: number;
    currentResults: number;
    isLoadingMore?: boolean;
    hasNextPage?: boolean;
    isIsolatedResult?: boolean;
    userInteracted?: boolean;
    onLoadMore?: () => void;
};

export const CategoryList: FC<Props> = ({
    categories,
    categoryTotalCount,
    currentResults,
    isLoadingMore,
    hasNextPage,
    isIsolatedResult,
    userInteracted,
    onLoadMore,
}) => {
    const { translate } = useTranslation();
    const infScrollIntersection = useRef<HTMLDivElement>(null);
    const [allowInfinity, setAllowInfinity] = useState<boolean>(false);
    const intersection = useIntersectionObserver(infScrollIntersection);

    useEffect(() => {
        if (intersection?.isIntersecting && allowInfinity && categoryTotalCount > currentResults) {
            loadMore();
        }
    }, [intersection?.isIntersecting]);

    const loadMore = () => {
        if (!allowInfinity) {
            setAllowInfinity(true);
        }
        onLoadMore?.();
    };

    useEffect(() => {
        if (allowInfinity && !userInteracted) {
            setAllowInfinity(false);
        }
    }, [userInteracted, allowInfinity]);

    if (!categories?.length) {
        return <></>;
    }

    return (
        <div>
            {!isIsolatedResult && (
                <h5>{formatString(translate('search.searchResultPage.categories'), categoryTotalCount || 0)}</h5>
            )}
            <Grid
                flow="row"
                columns={`repeat(2, minmax(0, 1fr))`}
                columnGap="small"
                rowGap="small"
                breakpoints={{
                    [mq('md')]: {
                        columns: `repeat(4, minmax(0, 1fr))`,
                    },
                    [mq('lg')]: {
                        columns: `repeat(6, minmax(0, 1fr))`,
                    },
                }}
            >
                {categories?.map((category) => (
                    <ResultCard
                        fallbackImageSrc={`${WEB_URL}/assets/kategori_billede_mangler.jpg`}
                        url={category.originalUrl}
                        key={key(category)}
                        trackingCode={category.trackingCode}
                        title={category.title}
                    />
                ))}
            </Grid>
            <div ref={infScrollIntersection} />
            {hasNextPage && !allowInfinity && (
                <LoadMore justifyContent="center">
                    <Button loading={isLoadingMore} onClick={loadMore}>
                        {formatString(translate('search.searchResultPage.showMore'), categoryTotalCount || 0)}
                    </Button>
                </LoadMore>
            )}
            {isLoadingMore && <Loading />}
        </div>
    );
};

const LoadMore = styled(Flex)(({ theme }) => ({
    margin: `${theme.space[8]} 0`,
}));
